// MortgageCalculatorV2.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Slider,
  InputAdornment,
  Paper,
  MenuItem,
  Divider,
  FormControlLabel,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from '@mui/material';

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const money0 = (n) => Number(n || 0).toLocaleString();
const money2 = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

function monthlyPI({ principal, annualRatePct, termYears }) {
  const n = termYears * 12;
  const r = (annualRatePct / 100) / 12;

  if (!principal || principal <= 0) return 0;
  if (!r) return Number((principal / n).toFixed(2));

  const pmt = (principal * r) / (1 - (1 + r) ** -n);
  return Number(pmt.toFixed(2));
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function MoneyField({ label, value, onChange, helperText, prefix = '$', suffix }) {
  return (
    <Box>
      {!!label && (
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
          {label}
        </Typography>
      )}
      <TextField
        value={money0(value)}
        onChange={(e) => {
          const raw = String(e.target.value).replace(/[^0-9]/g, '');
          onChange(Number(raw || 0));
        }}
        fullWidth
        helperText={helperText}
        InputProps={{
          startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : null,
          endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : null,
        }}
      />
    </Box>
  );
}

function PercentField({ label, value, onChange, helperText }) {
  return (
    <Box>
      {!!label && (
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
          {label}
        </Typography>
      )}
      <TextField
        value={String(value)}
        onChange={(e) => {
          const next = e.target.value.replace(/[^0-9.]/g, '');
          const parts = next.split('.');
          const sanitized = parts.length <= 2 ? next : `${parts[0]}.${parts.slice(1).join('')}`;
          onChange(sanitized);
        }}
        fullWidth
        inputProps={{ inputMode: 'decimal' }}
        helperText={helperText}
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
      />
    </Box>
  );
}

function LabeledSlider({ value, onChange, min, max, step, leftLabel, rightLabel }) {
  return (
    <Box sx={{ mt: 1 }}>
      <Slider value={value} onChange={(_, v) => onChange(Number(v))} min={min} max={max} step={step} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -1 }}>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {leftLabel}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {rightLabel}
        </Typography>
      </Box>
    </Box>
  );
}

function DonutBreakdown({ items, total, size = 170, ring = 20 }) {
  const safeTotal = Math.max(0.0001, total || 0);

  let acc = 0;
  const stops = items
    .filter((i) => i.value > 0)
    .map((i) => {
      const start = (acc / safeTotal) * 100;
      acc += i.value;
      const end = (acc / safeTotal) * 100;
      return `${i.color} ${start.toFixed(3)}% ${end.toFixed(3)}%`;
    })
    .join(', ');

  const bg = stops ? `conic-gradient(${stops})` : 'conic-gradient(#ddd 0% 100%)';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: bg,
          position: 'relative',
          boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: ring,
            borderRadius: '50%',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: 1,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 700 }}>
              Your Payment
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 900, lineHeight: 1.1 }}>
              ${money0(total)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const JET_DISCLAIMER_SECTIONS = [
  {
    title: 'Company & Program Disclosure',
    body: `© Jet HomeLoans, LP SM (“Jet”) NMLS ID# 1660135.
Corporate Office: 14701 Philips Highway, Suite 202, Jacksonville, FL 32256 | 833-270-7191.
Jet is an affiliate of Dream Finders Homes. Coventry Homes is a Dream Finders Homes company.
All products are subject to credit and property approval. Programs, rates, program terms and conditions subject to change without notice.
Not all products are available in all states or for all amounts. Other restrictions and limitations apply.
This is not a commitment to lend.
NMLS Consumer Access: www.nmlsconsumeraccess.org.`,
  },
  {
    title: 'Calculator Estimates',
    body: `The results of the Jet HomeLoans mortgage calculator are estimates only and are provided for informational purposes.
Calculator results do not constitute a Jet HomeLoans loan offer, commitment, approval, or guarantee of loan terms.
Calculations are based on the information entered by the user and standard assumptions.`,
  },
  {
    title: 'What Can Change',
    body: `Actual rates, APRs, loan terms, monthly payments, discount points, and costs may vary based on factors including, but not limited to:
credit profile, loan program, property type, occupancy, loan-to-value, market conditions, and applicable state or federal requirements.`,
  },
  {
    title: 'Not Included in Estimates',
    body: `Jet HomeLoans mortgage calculator results do not include all costs associated with a mortgage transaction, including:
lender fees, third-party fees, discount points, taxes, homeowners insurance, mortgage insurance, HOA fees (if applicable),
escrow requirements, or other applicable charges, and the actual payment obligation may be greater.`,
  },
  {
    title: 'No Relationship Created',
    body: `Use of the Jet HomeLoans mortgage calculator does not create a lender-borrower relationship.
To obtain accurate loan terms or determine eligibility, a loan application must be completed and reviewed by a Jet HomeLoans licensed mortgage professional.`,
  },
];

export default function MortgageCalculatorV2({ logoSrc = '/favicon/JET_singleline_color.jpg' }) {
  // core inputs
  const [homePrice, setHomePrice] = useState(380000);

  // Down payment dual-entry (keep in sync)
  const [downPaymentPctUI, setDownPaymentPctUI] = useState(20);
  const [downPaymentAmtUI, setDownPaymentAmtUI] = useState(Math.round((380000 * 20) / 100));
  const [downPaymentLastEdited, setDownPaymentLastEdited] = useState('pct'); // 'pct' | 'amt'

  const [interestRate, setInterestRate] = useState('6.82');
  const [loanProgram, setLoanProgram] = useState('30-year fixed');

  // loan type + VA
  const [loanType, setLoanType] = useState('conventional'); // conventional | fha | usda | va
  const [vaFirstTimeUse, setVaFirstTimeUse] = useState(true);
  const [vaFundingFeeExempt, setVaFundingFeeExempt] = useState(false);

  // buydown
  const [buydownType, setBuydownType] = useState('none'); // none | 1/0 | 2/1 | 3/2/1
  const [buydownCostOverride, setBuydownCostOverride] = useState(''); // optional override

  // yearly escrows
  const [propertyTaxesYear, setPropertyTaxesYear] = useState(3600);
  const [insuranceYear, setInsuranceYear] = useState(900);
  const [hoaMonth, setHoaMonth] = useState(0);

  // ---- Keep DP $ and % in sync ----
  useEffect(() => {
    const hp = safeNum(homePrice);

    if (downPaymentLastEdited === 'pct') {
      const pct = clamp(safeNum(downPaymentPctUI), 0, 100);
      const nextAmt = Math.round((hp * pct) / 100);
      if (nextAmt !== downPaymentAmtUI) setDownPaymentAmtUI(nextAmt);
    } else {
      const amt = clamp(safeNum(downPaymentAmtUI), 0, hp);
      const nextPct = hp ? (amt / hp) * 100 : 0;
      const roundedPct = Number(nextPct.toFixed(2));
      if (roundedPct !== Number(downPaymentPctUI)) setDownPaymentPctUI(roundedPct);
      if (amt !== downPaymentAmtUI) setDownPaymentAmtUI(amt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homePrice, downPaymentPctUI, downPaymentAmtUI, downPaymentLastEdited]);

  // If USDA, no 3/2/1
  useEffect(() => {
    if (loanType === 'usda' && buydownType === '3/2/1') setBuydownType('none');
  }, [loanType, buydownType]);

  const downPayment = useMemo(() => {
    const hp = safeNum(homePrice);
    const amt = clamp(safeNum(downPaymentAmtUI), 0, hp);
    return Math.round(amt);
  }, [homePrice, downPaymentAmtUI]);

  const downPaymentPct = useMemo(() => (homePrice ? (downPayment / homePrice) * 100 : 0), [homePrice, downPayment]);

  const baseLoanAmount = useMemo(() => Math.max(0, safeNum(homePrice) - safeNum(downPayment)), [homePrice, downPayment]);

  const termYears = useMemo(() => {
    if (loanProgram === '15-year fixed') return 15;
    if (loanProgram === '30-year fixed') return 30;
    if (loanProgram === '5/1 ARM') return 30;
    return 30;
  }, [loanProgram]);

  const termMonths = termYears * 12;

  // ---- Fees & MI rules ----
  const fhaUpfrontRate = 0.0175; // 1.75%
  const usdaGuaranteeRate = 0.01; // 1%
  const usdaMonthlyRate = 0.0035; // 0.35% annually
  const fhaAnnualMIPRate = useMemo(() => (downPaymentPct < 5 ? 0.0055 : 0.005), [downPaymentPct]);

  const convAnnualPMIRate = useMemo(() => {
    if (downPaymentPct >= 20) return 0;
    if (downPaymentPct < 5) return 0.01;
    if (downPaymentPct < 10) return 0.0065;
    if (downPaymentPct < 15) return 0.0035;
    return 0.0025;
  }, [downPaymentPct]);

  const vaFundingFeeRate = useMemo(() => {
    if (vaFundingFeeExempt) return 0;
    const dp = downPaymentPct;
    const tier = dp >= 10 ? '10' : dp >= 5 ? '5' : '0';
    if (tier === '0') return vaFirstTimeUse ? 0.0215 : 0.033;
    if (tier === '5') return 0.015;
    return 0.0125;
  }, [downPaymentPct, vaFirstTimeUse, vaFundingFeeExempt]);

  const financedFee = useMemo(() => {
    if (loanType === 'fha') return baseLoanAmount * fhaUpfrontRate;
    if (loanType === 'usda') return baseLoanAmount * usdaGuaranteeRate;
    if (loanType === 'va') return baseLoanAmount * vaFundingFeeRate;
    return 0;
  }, [loanType, baseLoanAmount, vaFundingFeeRate]);

  const totalLoanAmount = useMemo(() => baseLoanAmount + financedFee, [baseLoanAmount, financedFee]);

  const monthlyMI = useMemo(() => {
    if (!baseLoanAmount) return 0;

    if (loanType === 'fha') return Number(((baseLoanAmount * fhaAnnualMIPRate) / 12).toFixed(2));
    if (loanType === 'usda') return Number(((baseLoanAmount * usdaMonthlyRate) / 12).toFixed(2));
    if (loanType === 'conventional') {
      if (downPaymentPct >= 20) return 0;
      return Number(((baseLoanAmount * convAnnualPMIRate) / 12).toFixed(2));
    }
    return 0; // VA
  }, [loanType, baseLoanAmount, fhaAnnualMIPRate, usdaMonthlyRate, convAnnualPMIRate, downPaymentPct]);

  const miLabel = useMemo(() => {
    if (loanType === 'fha') return 'Monthly Mortgage Insurance (FHA MIP)';
    if (loanType === 'usda') return 'Monthly Fee (USDA Annual Fee)';
    if (loanType === 'conventional') return 'PMI (Estimated)';
    return 'Mortgage Insurance';
  }, [loanType]);

  const noteRate = useMemo(() => clamp(parseFloat(interestRate) || 0, 0, 30), [interestRate]);

  // ---- Buydown schedule ----
  const buydownSchedule = useMemo(() => {
    if (buydownType === 'none') {
      const pmt = monthlyPI({ principal: totalLoanAmount, annualRatePct: noteRate, termYears });
      return {
        type: 'none',
        rows: [{ years: 'All years', rate: noteRate, payment: pmt }],
        ongoingPayment: pmt,
        estimatedCost: 0,
      };
    }

    if (buydownType === '1/0') {
      const year1Rate = Math.max(0, noteRate - 1);
      const year1Pmt = monthlyPI({ principal: totalLoanAmount, annualRatePct: year1Rate, termYears });
      const ongoingPmt = monthlyPI({ principal: totalLoanAmount, annualRatePct: noteRate, termYears });
      const estimatedCost = (ongoingPmt - year1Pmt) * 12;

      return {
        type: '1/0',
        rows: [
          { years: 'Year 1', rate: year1Rate, payment: year1Pmt },
          { years: 'Year 2+', rate: noteRate, payment: ongoingPmt },
        ],
        ongoingPayment: ongoingPmt,
        estimatedCost: Math.max(0, estimatedCost),
      };
    }

    if (buydownType === '2/1') {
      const r1 = Math.max(0, noteRate - 2);
      const r2 = Math.max(0, noteRate - 1);
      const p1 = monthlyPI({ principal: totalLoanAmount, annualRatePct: r1, termYears });
      const p2 = monthlyPI({ principal: totalLoanAmount, annualRatePct: r2, termYears });
      const ongoingPmt = monthlyPI({ principal: totalLoanAmount, annualRatePct: noteRate, termYears });
      const estimatedCost = ((ongoingPmt - p1) + (ongoingPmt - p2)) * 12;

      return {
        type: '2/1',
        rows: [
          { years: 'Year 1', rate: r1, payment: p1 },
          { years: 'Year 2', rate: r2, payment: p2 },
          { years: 'Year 3+', rate: noteRate, payment: ongoingPmt },
        ],
        ongoingPayment: ongoingPmt,
        estimatedCost: Math.max(0, estimatedCost),
      };
    }

    // 3/2/1
    const r1 = Math.max(0, noteRate - 3);
    const r2 = Math.max(0, noteRate - 2);
    const r3 = Math.max(0, noteRate - 1);
    const p1 = monthlyPI({ principal: totalLoanAmount, annualRatePct: r1, termYears });
    const p2 = monthlyPI({ principal: totalLoanAmount, annualRatePct: r2, termYears });
    const p3 = monthlyPI({ principal: totalLoanAmount, annualRatePct: r3, termYears });
    const ongoingPmt = monthlyPI({ principal: totalLoanAmount, annualRatePct: noteRate, termYears });
    const estimatedCost = ((ongoingPmt - p1) + (ongoingPmt - p2) + (ongoingPmt - p3)) * 12;

    return {
      type: '3/2/1',
      rows: [
        { years: 'Year 1', rate: r1, payment: p1 },
        { years: 'Year 2', rate: r2, payment: p2 },
        { years: 'Year 3', rate: r3, payment: p3 },
        { years: 'Year 4+', rate: noteRate, payment: ongoingPmt },
      ],
      ongoingPayment: ongoingPmt,
      estimatedCost: Math.max(0, estimatedCost),
    };
  }, [buydownType, totalLoanAmount, noteRate, termYears]);

  const displayedBuydownCost = useMemo(() => {
    const manual = parseFloat(buydownCostOverride);
    if (!Number.isNaN(manual) && manual > 0) return manual;
    return Number(buydownSchedule.estimatedCost.toFixed(2));
  }, [buydownCostOverride, buydownSchedule.estimatedCost]);

  const principalAndInterest = buydownSchedule.ongoingPayment;

  // ---- Monthly escrows ----
  const propertyTaxesMonth = useMemo(() => Number((safeNum(propertyTaxesYear) / 12).toFixed(2)), [propertyTaxesYear]);
  const insuranceMonth = useMemo(() => Number((safeNum(insuranceYear) / 12).toFixed(2)), [insuranceYear]);

  // ---- Total payment ----
  const totalPayment = useMemo(() => {
    return Number(
      (
        safeNum(principalAndInterest) +
        safeNum(propertyTaxesMonth) +
        safeNum(insuranceMonth) +
        safeNum(monthlyMI) +
        safeNum(hoaMonth)
      ).toFixed(2)
    );
  }, [principalAndInterest, propertyTaxesMonth, insuranceMonth, monthlyMI, hoaMonth]);

  // ---- Donut items ----
  const donutItems = useMemo(
    () => [
      { key: 'pi', label: 'P&I', value: safeNum(principalAndInterest), color: '#b49760' },
      { key: 'tax', label: 'Taxes', value: safeNum(propertyTaxesMonth), color: '#79653b' },
      { key: 'ins', label: "Homeowner's Insurance", value: safeNum(insuranceMonth), color: '#d3b66a' },
      { key: 'mi', label: miLabel.includes('PMI') ? 'PMI' : 'MI', value: safeNum(monthlyMI), color: '#f2dd9b' },
      { key: 'hoa', label: 'HOA', value: safeNum(hoaMonth), color: '#fff2c7' },
    ],
    [principalAndInterest, propertyTaxesMonth, insuranceMonth, monthlyMI, miLabel, hoaMonth]
  );

  const lineItem = (label, value) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Typography variant="body2" sx={{ opacity: 0.85 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  );

  const handlePrint = () => window.print();

  // Only show 3/2/1 if not USDA
  const show321 = loanType !== 'usda';

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
      {/* Print CSS: print summary + full-width disclaimer */}
      <style>
        {`
   @media print {
  /* Remove non-print elements from layout entirely */
  .mc-no-print { display: none !important; }

  /* Hide the navbar, footer, and CTA outside the calculator */
  nav, footer, header { display: none !important; }

  /* Hide everything by default */
  body * { visibility: hidden !important; }

  /* Only show summary + disclaimer */
  #mc-print-area,
  #mc-print-area *,
  #mc-disclaimer,
  #mc-disclaimer * {
    visibility: visible !important;
  }

  /* Force the grid to behave like a single column in print */
  #mc-layout {
    display: block !important;
  }

  /* Make grid items full width in print */
  #mc-layout .MuiGrid-item {
    width: 100% !important;
    max-width: 100% !important;
    flex-basis: auto !important;
  }

  /* Summary: normal flow (NO absolute positioning) */
  #mc-print-area {
    position: static !important;
    left: auto !important;
    top: auto !important;
    width: 100% !important;
    padding: 0 !important;
    font-size: 11px !important;
    transform: none !important;
    border-left: none !important;
  }

  /* Shrink logo for print */
  #mc-print-area img {
    max-height: 50px !important;
    height: 50px !important;
    width: auto !important;
  }

  /* Shrink the logo container shadow/padding */
  #mc-print-area > div:first-child {
    box-shadow: none !important;
    padding: 6px !important;
    margin-bottom: 8px !important;
  }

  /* Shrink donut chart */
  #mc-print-area .mc-donut-wrap {
    transform: scale(0.7);
    transform-origin: center center;
    margin: -10px 0 !important;
  }

  /* Reduce spacing in print area */
  #mc-print-area .MuiDivider-root {
    margin-top: 6px !important;
    margin-bottom: 6px !important;
  }

  #mc-print-area .MuiTypography-root {
    line-height: 1.3 !important;
  }

  /* Reduce the big payment font */
  #mc-print-area .mc-total-payment {
    font-size: 24px !important;
  }

  /* Loan Amount Summary box */
  #mc-print-area .mc-loan-summary {
    padding: 8px !important;
    margin-top: 8px !important;
  }

  /* Disclaimer: stacked underneath summary, compact */
  #mc-disclaimer {
    position: static !important;
    left: auto !important;
    top: auto !important;
    width: 100% !important;
    margin-top: 8px !important;
    padding: 8px !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  #mc-disclaimer .MuiTypography-root {
    font-size: 9px !important;
    line-height: 1.25 !important;
  }

  /* Remove extra Paper spacing/shadows in print */
  .MuiPaper-root {
    box-shadow: none !important;
    padding: 0 !important;
  }

  @page {
    size: portrait;
    margin: 8mm;
  }
}
        `}
      </style>

      {/* Header (UX view) */}
      <Box className="mc-no-print" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 900 }}>Mortgage Calculator</Typography>
      </Box>

      <Grid container spacing={2} id="mc-layout">
        {/* LEFT: Inputs */}
        <Grid item xs={12} md={7}>
          <Box className="mc-no-print" sx={{ pr: { md: 2 } }}>
            <MoneyField label="Home Price" value={homePrice} onChange={setHomePrice} />
            <LabeledSlider
              value={homePrice}
              onChange={setHomePrice}
              min={100000}
              max={1000000}
              step={1000}
              leftLabel="$100,000"
              rightLabel="$1,000,000"
            />

            {/* ✅ Down payment moved up + $ and % */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                Down Payment
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <MoneyField
                    label="Down Payment ($)"
                    value={downPaymentAmtUI}
                    onChange={(v) => {
                      setDownPaymentLastEdited('amt');
                      setDownPaymentAmtUI(v);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <PercentField
                    label="Down Payment (%)"
                    value={downPaymentPctUI}
                    onChange={(v) => {
                      setDownPaymentLastEdited('pct');
                      setDownPaymentPctUI(v);
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Synced
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    ${money0(downPayment)} ({downPaymentPct.toFixed(2)}%)
                  </Typography>
                </Box>
                <LabeledSlider
                  value={Number(downPaymentPctUI) || 0}
                  onChange={(v) => {
                    setDownPaymentLastEdited('pct');
                    setDownPaymentPctUI(v);
                  }}
                  min={0}
                  max={30}
                  step={0.25}
                  leftLabel="0%"
                  rightLabel="30%"
                />
              </Box>
            </Box>

            {/* Loan Program now below DP */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                Loan Program
              </Typography>
              <TextField select value={loanProgram} onChange={(e) => setLoanProgram(e.target.value)} fullWidth>
                <MenuItem value="30-year fixed">30-Year Fixed</MenuItem>
                <MenuItem value="15-year fixed">15-Year Fixed</MenuItem>
                <MenuItem value="5/1 ARM">5/1 ARM</MenuItem>
              </TextField>
            </Box>

            {/* Loan type + buydown */}
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                  Loan Type
                </Typography>
                <TextField select value={loanType} onChange={(e) => setLoanType(e.target.value)} fullWidth>
                  <MenuItem value="conventional">Conventional</MenuItem>
                  <MenuItem value="fha">FHA</MenuItem>
                  <MenuItem value="usda">USDA</MenuItem>
                  <MenuItem value="va">VA</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                  Buydown
                </Typography>
                <TextField select value={buydownType} onChange={(e) => setBuydownType(e.target.value)} fullWidth>
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="1/0">1/0 Buydown</MenuItem>
                  <MenuItem value="2/1">2/1 Buydown</MenuItem>
                  {show321 && <MenuItem value="3/2/1">3/2/1 Buydown</MenuItem>}
                </TextField>
                {loanType === 'usda' && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.75 }}>
                    3/2/1 is not available for USDA in this calculator view.
                  </Typography>
                )}
              </Grid>
            </Grid>

            {loanType === 'va' && (
              <Box
                sx={{
                  mt: 2,
                  border: '1px solid rgba(0,0,0,0.10)',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography fontWeight={800} gutterBottom>
                  VA Funding Fee Options
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={vaFirstTimeUse}
                      onChange={(e) => setVaFirstTimeUse(e.target.checked)}
                      disabled={vaFundingFeeExempt}
                    />
                  }
                  label="First-time use"
                />
                <FormControlLabel
                  control={<Checkbox checked={vaFundingFeeExempt} onChange={(e) => setVaFundingFeeExempt(e.target.checked)} />}
                  label="Funding fee exempt (disability/other)"
                />
                <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                  Funding fee is financed into the loan amount when applicable.
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Interest Rate
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {noteRate.toFixed(2)}%
                </Typography>
              </Box>

              <PercentField label="" value={interestRate} onChange={setInterestRate} />
              <LabeledSlider
                value={noteRate}
                onChange={(v) => setInterestRate(String(v))}
                min={0}
                max={15}
                step={0.01}
                leftLabel="0%"
                rightLabel="15%"
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Property Taxes / year
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  ${money0(propertyTaxesYear)}
                </Typography>
              </Box>
              <MoneyField label="" value={propertyTaxesYear} onChange={setPropertyTaxesYear} />
              <LabeledSlider
                value={propertyTaxesYear}
                onChange={setPropertyTaxesYear}
                min={0}
                max={20000}
                step={25}
                leftLabel="$0"
                rightLabel="$20,000"
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Homeowner&apos;s Insurance / year
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  ${money0(insuranceYear)}
                </Typography>
              </Box>
              <MoneyField label="" value={insuranceYear} onChange={setInsuranceYear} />
              <LabeledSlider value={insuranceYear} onChange={setInsuranceYear} min={0} max={6000} step={10} leftLabel="$0" rightLabel="$6,000" />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  HOA Dues / month
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  ${money0(hoaMonth)}
                </Typography>
              </Box>
              <MoneyField label="" value={hoaMonth} onChange={setHoaMonth} />
              <LabeledSlider value={hoaMonth} onChange={setHoaMonth} min={0} max={1000} step={5} leftLabel="$0" rightLabel="$1,000" />
            </Box>

            <Divider sx={{ my: 2 }} />
          </Box>
        </Grid>

        {/* RIGHT: Summary (prints) */}
        <Grid item xs={12} md={5}>
          <Box
            id="mc-print-area"
            sx={{
              pl: { md: 2 },
              borderLeft: { md: '1px solid rgba(0,0,0,0.08)' },
              height: '100%',
            }}
          >
            {/* Logo at top of summary (visible on screen + print) */}
            <Box
              sx={{
                borderRadius: 3,
                p: 2,
                mb: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              <Box
                component="img"
                src={logoSrc}
                alt="Jet HomeLoans"
                sx={{
                  height: { xs: 60, md: 80 },
                  width: '100%',
                  maxWidth: 360,
                  objectFit: 'contain',
                }}
                onError={(e) => {
                  e.currentTarget.alt = `Logo failed to load: ${logoSrc}`;
                  e.currentTarget.style.border = '2px solid red';
                  e.currentTarget.style.padding = '6px';
                }}
              />
            </Box>

            {/* Print-only header text */}
            <Box className="mc-print-only" sx={{ mb: 2, display: 'none' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }}>Estimated Payment</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Mortgage Calculator Summary
              </Typography>
            </Box>

            <Typography variant="overline" sx={{ opacity: 0.75 }}>
              Estimated Payment
            </Typography>

            <Typography className="mc-total-payment" sx={{ fontSize: 34, fontWeight: 900, lineHeight: 1.1, mb: 1 }}>
              ${money0(totalPayment)}/month
            </Typography>

            <Divider sx={{ my: 2 }} />

            {lineItem('Loan Amount', `$${money0(totalLoanAmount)}`)}
            {lineItem('Loan Term', `${termMonths} months`)}
            {lineItem('Down Payment', `$${money0(downPayment)} (${downPaymentPct.toFixed(2)}%)`)}
            {lineItem('Interest Rate', `${noteRate.toFixed(2)}%`)}
            {lineItem('Principal & Interest', `$${money2(principalAndInterest)}/month`)}
            {lineItem('Property Taxes', `$${money2(propertyTaxesMonth)}/month`)}
            {lineItem("Homeowner's Insurance", `$${money2(insuranceMonth)}/month`)}
            {lineItem('HOA Dues', `$${money2(hoaMonth)}/month`)}
            {lineItem(miLabel, `$${money2(monthlyMI)}/month`)}

            <Divider sx={{ my: 2 }} />

            <Box className="mc-donut-wrap">
              <DonutBreakdown items={donutItems} total={totalPayment} />

              {/* Legend */}
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={1}>
                  {donutItems.map((i) => (
                    <Grid item xs={6} key={i.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: i.color,
                            border: '1px solid rgba(0,0,0,0.08)',
                          }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {i.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* Buydown schedule table */}
            {buydownType !== 'none' && Array.isArray(buydownSchedule.rows) && buydownSchedule.rows.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography fontWeight={900} sx={{ mb: 1 }}>
                  Buydown Payment Schedule
                </Typography>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 900 }}>Period</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Rate</TableCell>
                      <TableCell sx={{ fontWeight: 900 }} align="right">
                        P&amp;I
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {buydownSchedule.rows.map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{r.years}</TableCell>
                        <TableCell>{Number(r.rate).toFixed(2)}%</TableCell>
                        <TableCell align="right">${money2(r.payment)}/mo</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    Estimated Buydown Cost: <b>${money2(displayedBuydownCost)}</b>
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>
                    Estimate = total of (ongoing payment − reduced payment) across buydown years × 12.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Financed fee summary */}
            <Box className="mc-loan-summary" sx={{ mt: 3, p: 2, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <Typography fontWeight={900} sx={{ mb: 1 }}>
                Loan Amount Summary
              </Typography>
              {lineItem('Base Loan Amount', `$${money0(baseLoanAmount)}`)}
              {lineItem(
                `Financed Fee${
                  loanType === 'fha'
                    ? ' (FHA UFMIP 1.75%)'
                    : loanType === 'usda'
                      ? ' (USDA Guarantee 1.00%)'
                      : loanType === 'va'
                        ? ' (VA Funding Fee)'
                        : ''
                }`,
                `$${money2(financedFee)}`
              )}
              <Divider sx={{ my: 1 }} />
              {lineItem('Total Loan Amount (calc)', `$${money2(totalLoanAmount)}`)}
            </Box>
          </Box>
        </Grid>

 {/* ✅ Call-to-action below calculator */}
      <Grid item xs={12}>
        <Box
          className="mc-no-print"
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button variant="outlined" onClick={handlePrint} sx={{ fontWeight: 900 }}>
            Print / Save PDF
          </Button>
        </Box>
      </Grid>

      
        {/* ✅ FULL-WIDTH DISCLAIMER (spans both columns) */}
        <Grid item xs={12}>
          <Box
            id="mc-disclaimer"
            sx={{
              mt: 1,
              p: 2,
              borderRadius: 2,
              border: '1px solid rgba(0,0,0,0.10)',
              backgroundColor: 'rgba(0,0,0,0.02)',
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: 1.35,
                opacity: 0.85,
                fontWeight: 700,
                mb: 1.25,
              }}
            >
              Estimates for demonstration only. PMI/MIP varies by guidelines, credit, occupancy, and lender.
            </Typography>

            <Box sx={{ display: 'grid', gap: 1.25 }}>
              {JET_DISCLAIMER_SECTIONS.map((sec) => (
                <Box key={sec.title}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      opacity: 0.9,
                      mb: 0.25,
                    }}
                  >
                    {sec.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 11.5,
                      lineHeight: 1.4,
                      opacity: 0.85,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {sec.body}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>

     
    </Paper>
  );
}